import * as React from "react";
import {t} from "i18next";
import {
    ModeltypeBasicExportStatus,
    OptionBasicCoreDto,
    OptionBasicDtoTextExportStatus,
    OptionDtoTextExportStatus
} from "../../services/Client";
import {Icon, Popup} from "semantic-ui-react";
import {PDiPFilterEnum, PCCDFilterEnum} from "./VirtualizedTableEnums";
import {
    ILocalColumnSizeSettings,
    ITextExportStatus,
    IVirtualizedTableColumn,
    TData
} from "./VirtualizedTableInterfaces";
import {
    ConstraintKey,
    ExclusionKey,
    OptionColumnsTwoLanguage,
    PackageContentKey
} from "../../views/OptionsView/OptionColumns";

// Constants
export const PDiPFlag = "pdipFlag";

export const PCCDFlag = "pccdFlag";

export const virtualizedTableId = 'virtualized-table-id';

// Functions
export const keepRowSorting = (keys: string[], newOrder: IVirtualizedTableColumn[]) => {
    const updatedOrder = [...newOrder];
    updatedOrder.sort((a, b) => {
        return keys.findIndex((item) => item === a.key) - keys.findIndex((item) => item === b.key);
    });
    return updatedOrder;
};

export const getPDiPFilterLabel = (value: string, forButton?: boolean) => {
    switch (value) {
        case (PDiPFilterEnum.SHOW_PDIP_VALID):
            return forButton ? t("GLOBAL:PDIP_VALID") : t("GLOBAL:SHOW_PDIP_VALID");
        case (PDiPFilterEnum.SHOW_PDIP_INVALID):
            return forButton ? t("GLOBAL:PDIP_INVALID") : t("GLOBAL:SHOW_PDIP_INVALID");
        default:
            return forButton ? t("GLOBAL:ALL") : t("GLOBAL:SHOW_ALL");
    }
};

export const getPCCDFilterLabel = (value: string, forButton?: boolean) => {
    switch (value) {
        case (PCCDFilterEnum.SHOW_PCCD_VALID):
            return forButton ? t("GLOBAL:PCCD_VALID") : t("GLOBAL:SHOW_PCCD_VALID");
        case (PCCDFilterEnum.SHOW_PCCD_INVALID):
            return forButton ? t("GLOBAL:PCCD_INVALID") : t("GLOBAL:SHOW_PCCD_INVALID");
        default:
            return forButton ? t("GLOBAL:ALL") : t("GLOBAL:SHOW_ALL");
    }
};

export const scrollGridToTop = (): void => {
    if (document.getElementsByClassName('ReactVirtualized__Grid')[1]) {
        document.getElementsByClassName('ReactVirtualized__Grid')[1].scrollTo({top: 0});
    }
};

// Components
export const buildTextExportStatusComponent = (type: ITextExportStatus) => {
    switch (type.textExportStatus) {
        case OptionBasicDtoTextExportStatus.NO_PCCD_TEXT:
        case OptionBasicDtoTextExportStatus.NO_PCCD_GENERAL_TEXT:
        case OptionBasicDtoTextExportStatus.OB_PCCD_EXPORT_ERROR:
        case OptionBasicDtoTextExportStatus.PROMT_OB_ERROR:
        case OptionDtoTextExportStatus.O_PCCD_EXPORT_ERROR:
        case OptionDtoTextExportStatus.PROMT_O_ERROR:
            return (
                <Popup
                    position="top right"
                    on='hover'
                    trigger={<Icon name="exclamation circle" size="small" color="red" />}
                    flowing={true}
                    hoverable={true}
                    style={{maxWidth: "510px"}}
                >
                    <div >
                        <p><b>{t("EXPORT:STATUS") + ": "}</b> {t("EXPORT:ERROR")} </p>
                        <p><b>{t("EXPORT:REASON") + ": "}</b> {t("EXPORT:" + type.textExportStatus)} </p>
                        {type.textExportDate && <p><b>{t("EXPORT:TIMESTAMP") + ": "}</b> {new Date(type.textExportDate).toString()}  </p>}
                    </div>
                </Popup>
            );

        case OptionDtoTextExportStatus.UNKNOWN_PCCD_BTYP:
            return (
                <Popup
                    position={"top right"}
                    on={"hover"}
                    trigger={<Icon name="warning sign" size="small" color="yellow" />}
                    flowing={true}
                    hoverable={true}
                    style={{maxWidth: "510px"}}
                >
                    <div >
                        <p><b>{t("EXPORT:STATUS") + ": "}</b> {t("EXPORT:ERROR")} </p>
                        <p><b>{t("EXPORT:REASON") + ": "}</b> {t("EXPORT:" + type.textExportStatus)} </p>
                        <p><b>{t("EXPORT:UNKNOWN_BTYPS_LIST") + ": "}</b> {type.textExportStatusMessage} </p>
                        {type.textExportDate &&
                            <p><b>{t("EXPORT:TIMESTAMP") + ": "}</b> {new Date(type.textExportDate).toString()}  </p>}
                    </div>
                </Popup>
            );

        case OptionBasicDtoTextExportStatus.OB_DUPLICATE_PCCD_IDENTIFIER:
        case OptionBasicDtoTextExportStatus.OB_NOT_APPROVED:
        case OptionBasicDtoTextExportStatus.OB_NOT_APPROVED_O_EXPORTED:
        case OptionDtoTextExportStatus.O_DUPLICATE_PCCD_IDENTIFIER:
        case OptionDtoTextExportStatus.O_NOT_APPROVED:
        case OptionDtoTextExportStatus.NO_MB_VALID_ASSIGNMENT:
        case OptionDtoTextExportStatus.OB_O_TEXTS_IDENTICAL:
            return (
                <Popup
                    position="top right"
                    on='hover'
                    trigger={<Icon name="warning sign" size="small" color="yellow" />}
                    flowing={true}
                    hoverable={true}
                    style={{maxWidth: "510px"}}
                >
                    <div >
                        <p><b>{t("EXPORT:STATUS") + ": "}</b> {t("EXPORT:WARNING")} </p>
                        <p><b>{t("EXPORT:REASON") + ": "}</b> {t("EXPORT:" + type.textExportStatus)} </p>
                        {type.textExportDate && <p><b>{t("EXPORT:TIMESTAMP") + ": "}</b> {new Date(type.textExportDate).toString()}  </p>}
                    </div>
                </Popup>
            );

        case OptionBasicDtoTextExportStatus.SER_OPTION_TYPE:
        case OptionBasicDtoTextExportStatus.TEQ_OPTION_TYPE:
            return (
                <Popup
                    position="top right"
                    on='hover'
                    trigger={<Icon name="info circle" size="small" color="blue" />}
                    flowing={true}
                    hoverable={true}
                    style={{maxWidth: "510px"}}
                >
                    <div >
                        <p><b>{t("EXPORT:STATUS") + ": "}</b> {t("EXPORT:NOT_SYNCRONISABLE")} </p>
                        <p><b>{t("EXPORT:INFO") + ": "}</b> {t("EXPORT:" + type.textExportStatus)} </p>
                        {type.textExportDate && <p><b>{t("EXPORT:TIMESTAMP") + ": "}</b> {new Date(type.textExportDate).toString()}  </p>}
                    </div>
                </Popup>
            );

        case OptionBasicDtoTextExportStatus.OB_EXPORT_SUCCESS:
        case OptionDtoTextExportStatus.O_EXPORT_SUCCESS:
        case OptionBasicDtoTextExportStatus.PCCD_PROMT_IDENTICAL:
            return (
                <Popup
                    position="top right"
                    on='hover'
                    trigger={<Icon name="check circle" size="small" color="green" />}
                    flowing={true}
                    hoverable={true}
                    style={{maxWidth: "510px"}}
                >
                    <div >
                        <p><b>{t("EXPORT:STATUS") + ": "}</b> {t("EXPORT:SUCCESS")} </p>
                        <p><b>{t("EXPORT:INFO") + ": "}</b> {t("EXPORT:" + type.textExportStatus)} </p>
                        {type.textExportDate && <p><b>{t("EXPORT:TIMESTAMP") + ": "}</b> {new Date(type.textExportDate).toString()}  </p>}
                    </div>
                </Popup>
            );

        case OptionBasicDtoTextExportStatus.OUT_OF_RANGE_MODELYEAR:
        case OptionDtoTextExportStatus.INVALID_EXPORT_MARKET:
            return (
                <Popup
                    position="top right"
                    on='hover'
                    trigger={<Icon name="x" size="small" color="red" />}
                    flowing={true}
                    hoverable={true}
                    style={{maxWidth: "510px"}}
                >
                    <div >
                        <p><b>{t("EXPORT:STATUS") + ": "}</b> {t("EXPORT:WARNING")} </p>
                        <p><b>{t("EXPORT:REASON") + ": "}</b> {t("EXPORT:" + type.textExportStatus)} </p>
                        {type.textExportDate && <p><b>{t("EXPORT:TIMESTAMP") + ": "}</b> {new Date(type.textExportDate).toString()}  </p>}
                    </div>
                </Popup>
            );

        case OptionDtoTextExportStatus.PENDING_DELIVERY_PROCESSING:
            var nextJobRunTime = new Date();

            // TODO: Replace hardcoding?
            if (new Date().getHours() < 2) {
                nextJobRunTime.setHours(2, 0, 0);
            } else {
                nextJobRunTime.setDate(nextJobRunTime.getDate() + 1);
                nextJobRunTime.setHours(2, 0, 0);
            }

            return (
                <Popup
                    position="top right"
                    on='hover'
                    trigger={<Icon name="wait" size="small" color="grey" />}
                    flowing={true}
                    hoverable={true}
                    style={{maxWidth: "510px"}}
                >
                    <div >
                        <p><b>{t("EXPORT:STATUS") + ": "}</b> {t("EXPORT:PENDING_DELIVERY_PROCESSING")} </p>
                        <p><b>{t("EXPORT:INFO") + ": "}</b> {t("EXPORT:PENDING_DELIVERY_PROCESSING_INFO")} </p>
                        <p><b>{t("EXPORT:NEXT_JOB_RUN") + ": "}</b> {nextJobRunTime.toString()} </p>
                    </div>
                </Popup>
            );

        default:
            return null;
    }
};

// Components
export const buildPCCDStatusComponent = (type: ITextExportStatus) => {
    switch (type.textExportStatus) {
        case ModeltypeBasicExportStatus.DERIVATE_EXPORT_PENDING:
            return (
                <Popup
                    position="top right"
                    on='hover'
                    trigger={<Icon name="wait" size="small" color="grey" />}
                    flowing={true}
                    hoverable={true}
                    style={{maxWidth: "510px"}}
                >
                    <div >
                        <p><b>{t("EXPORT:STATUS") + ": "}</b> {t("EXPORT:PENDING_DELIVERY_PROCESSING")} </p>
                        <p><b>{t("EXPORT:REASON") + ": "}</b> {t("EXPORT:" + type.textExportStatus)} </p>
                        {type.textExportDate && <p><b>{t("EXPORT:TIMESTAMP") + ": "}</b> {new Date(type.textExportDate).toString()}  </p>}
                    </div>
                </Popup>
            );

        case ModeltypeBasicExportStatus.DERIVATE_PCCD_EXPORT_ERROR:
            return (
                <Popup
                    position="top right"
                    on='hover'
                    trigger={<Icon name="check circle" size="small" color="red" />}
                    flowing={true}
                    hoverable={true}
                    style={{maxWidth: "510px"}}
                >
                    <div >
                        <p><b>{t("EXPORT:STATUS") + ": "}</b> {t("EXPORT:ERROR")} </p>
                        <p><b>{t("EXPORT:REASON") + ": "}</b> {t("EXPORT:" + type.textExportStatus)} </p>
                        {type.textExportDate && <p><b>{t("EXPORT:TIMESTAMP") + ": "}</b> {new Date(type.textExportDate).toString()}  </p>}
                    </div>
                </Popup>
            );
        case ModeltypeBasicExportStatus.DERIVATE_PCCD_EXPORT_SUCCESS:
        case ModeltypeBasicExportStatus.DERIVATE_PCCD_IDENTICAL:
            return (
                <Popup
                    position="top right"
                    on='hover'
                    trigger={<Icon name="check circle" size="small" color="green" />}
                    flowing={true}
                    hoverable={true}
                    style={{maxWidth: "510px"}}
                >
                    <div >
                        <p><b>{t("EXPORT:STATUS") + ": "}</b> {t("EXPORT:SUCCESS")} </p>
                        <p><b>{t("EXPORT:REASON") + ": "}</b> {t("EXPORT:" + type.textExportStatus)} </p>
                        {type.textExportDate && <p><b>{t("EXPORT:TIMESTAMP") + ": "}</b> {new Date(type.textExportDate).toString()}  </p>}
                    </div>
                </Popup>
            );

        case ModeltypeBasicExportStatus.DERIVATE_NO_PCCD_TEXT:
            return (
                <Popup
                    position="top right"
                    on='hover'
                    trigger={<Icon name="x" size="small" color="yellow" />}
                    flowing={true}
                    hoverable={true}
                    style={{maxWidth: "510px"}}
                >
                    <div >
                        <p><b>{t("EXPORT:STATUS") + ": "}</b> {t("EXPORT:WARNING")} </p>
                        <p><b>{t("EXPORT:REASON") + ": "}</b> {t("EXPORT:" + type.textExportStatus)} </p>
                        {type.textExportDate && <p><b>{t("EXPORT:TIMESTAMP") + ": "}</b> {new Date(type.textExportDate).toString()}  </p>}
                    </div>
                </Popup>
            );
        default:
            return null;
    }
};

/**
 * This function is used to measure the highest cell of a row, which will also set the height of the row itself
 * In each view, Base Options and Options, there is a hidden div with the id hidden-long-text-box.
 * This div contains two <p> tags which have some styling applied to mimic the textarea which is actually displayed
 * in the table cell.
 * There are 2 because some of the cells have two text areas for each language, but if the cells contains only one
 * textarea, then a single <p> can be used.
 *
 * The idea is to set, programmatically (this is an anti-practice for react, in fact. try to avoid it as much as
 * possible), the content of the <p> tags with the value that is also used for the textareas, and then getting the
 * height value of the element, in order to know before-hand how high is the row.
 *
 * So, as an example, we take shorttexts, we put the values inside the <p>'s, we measure their height then we clear
 * their content. Then, we take longtext and we put again the values inside the <p>'s and we measure again the
 * height. And so on.
 *
 * @param item
 *        the row data
 * @param hiddenDiv
 *        the hidden div element used for measurements
 * @param columns
 *        the visible columns that needs to be measured
 * @param columnSizeSetting
 *        the columns with their width size
 *
 * @return the height of the highest cell which will give the height of the row
 */
export const computeRowHeightInFullView = (
    item: TData,
    columns: IVirtualizedTableColumn[],
    columnSizeSetting: ILocalColumnSizeSettings,
) => {
    // we set the default row height, which is 100
    let rowHeight = 100;

    const hiddenDiv = document.getElementById('hidden-long-text-box');

    if (hiddenDiv) {
        hiddenDiv.style.display = 'initial';

        // we go thru each column (cell) that needs to be measured
        columns.forEach(col => {
            // we set the width of the div as the width of the column stored in localstorage
            hiddenDiv.setAttribute(
                "style",
                `width:${columnSizeSetting[col.key].width}px`
            );

            // if the columns are of type twoLanguage, we need to take into account both EN and DE <p>'s
            // if not, then it's enough to use only one <p> element and ignore the other one
            if (OptionColumnsTwoLanguage.includes(col.key)) {
                // we put the gb value into the gb <p> tag
                // @ts-ignore
                document.getElementById('textarea-en').innerText = item[col.key][0].value;

                // we put the de value into the de <p> tag
                // @ts-ignore
                document.getElementById('textarea-de').innerText = item[col.key][1].value;

                // we take the height value of the div after the texts have been assigned
                // if the height is higher than the current maximum rowHeight, then we store it as the new
                // maximum height, if not, we keep the current maximum value
                rowHeight = hiddenDiv.clientHeight > rowHeight ? hiddenDiv.clientHeight : rowHeight;

                // we clear the DE <p> tag (the en one we clear at the end)
                // @ts-ignore
                document.getElementById('textarea-de').innerText = '';
            } else {
                // we check to see if the current value is one for the constrain, exclusion or packageContent column
                // if it is, then we map the data accordingly
                // then we assign the value to the EN <p> tag (we only need one <p> tag)
                // @ts-ignore
                document.getElementById('textarea-en').innerText =
                    [ConstraintKey, ExclusionKey, PackageContentKey].includes(col.key)
                        // @ts-ignore
                        ? item[col.key] && item[col.key].map((ob: OptionBasicCoreDto) => ob.optionCode + '-' + ob.optionType).join(", ")
                        : item[col.key];

                // we take the height of the div after the text has been assigned
                // if the resulted height is higher than the current maximum rowHeight, then we store it as the new
                // maximum height, if not, we keep the current maximum value
                rowHeight = hiddenDiv.clientHeight > rowHeight ? hiddenDiv.clientHeight : rowHeight;
            }

            // we clear the EN <p> tag
            // @ts-ignore
            document.getElementById('textarea-en').innerText = '';
        });

        hiddenDiv.style.display = 'none';
    }
    return rowHeight;
};



// WEBPACK FOOTER //
// ./src/components/Table/VirtualizedTableUtils.tsx